import { type DonationModel } from '@/shared/api'
import { TABLE_PAGE_SIZE_OPTIONS } from '@/shared/lib/query'
import { TableWrapper } from '@/shared/ui'
import { Table, type TableProps } from 'antd'
import { useUnit } from 'effector-react'

import { type DonationsByDateSortState } from '../../lib/donations-by-date-model'
import {
  $donationsByDateTablePage,
  $donationsByDateTablePageSize,
  $donationsByDateTableSort,
  donationsByDateTablePageChanged,
  donationsByDateTablePageReset,
  donationsByDateTablePageSizeChanged,
  donationsByDateTableQuery,
  donationsByDateTableSortChanged,
  donationsByDateTableSortReset,
} from '../../model/donations-by-date-table.model'
import { useDonationsByDateTableColumns } from './useDonationsByDateTableColumns'

interface DonationsByDateTableProps {
  onDetails: (userId?: string | null) => void
}
export const DonationsByDateTable = ({
  onDetails: handleDetails,
}: DonationsByDateTableProps) => {
  const { data, pending } = useUnit(donationsByDateTableQuery)

  const sort = useUnit($donationsByDateTableSort)
  const changeSort = useUnit(donationsByDateTableSortChanged)
  const resetSort = useUnit(donationsByDateTableSortReset)

  const page = useUnit($donationsByDateTablePage)
  const changePage = useUnit(donationsByDateTablePageChanged)
  const resetPage = useUnit(donationsByDateTablePageReset)
  const pageSize = useUnit($donationsByDateTablePageSize)
  const changePageSize = useUnit(donationsByDateTablePageSizeChanged)

  const tableColumns: TableProps<DonationModel>['columns'] =
    useDonationsByDateTableColumns({ sort, handleDetails })

  if (!data?.items || !data.items.length) {
    return null
  }

  const onTableChange: TableProps<DonationModel>['onChange'] = (
    pagination,
    _filters,
    sorter,
  ) => {
    // Sort
    if (!Array.isArray(sorter)) {
      const field = sorter.field as string | undefined
      const order = sorter.order as 'ascend' | 'descend' | undefined

      if (field && order) {
        const sortOrder = order === 'ascend' ? 'asc' : 'desc'

        if (sort.field !== field || sort.order !== sortOrder) {
          changeSort({
            field: field as DonationsByDateSortState['field'],
            order: sortOrder,
          })
          resetPage()
        }
      } else if (sort.field !== null || sort.order !== null) {
        resetSort()
        resetPage()
      }
    }
    // Pagination
    if (pagination.current && pagination.pageSize) {
      if (pagination.current !== page) {
        changePage(pagination.current)
      }
      if (pagination.pageSize !== pageSize) {
        changePageSize(pagination.pageSize)
        resetPage()
      }
    }
  }

  return (
    <TableWrapper>
      <Table
        sticky
        showSorterTooltip={false}
        rowKey={(record) => `${record.userId}_${record.date}`}
        bordered
        columns={tableColumns}
        dataSource={data.items}
        loading={pending}
        onChange={onTableChange}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: data?.totalItems,
          showSizeChanger: true,
          pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
          locale: { items_per_page: 'Пожертвований на странице' },
        }}
      />
    </TableWrapper>
  )
}
